import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Key, Layers, Link2, MessageSquare, Shield } from 'lucide-react';
import { Chip, type EmployeeView } from '../shared';
import { ProfileCard } from './ProfileCard.js';

// ── Allocation strip (5 cells) ──────────────────────────────────────────────

const Cell: React.FC<{ label: string; value: React.ReactNode; sub: string }> = ({ label, value, sub }) => (
  <div className="bg-surface px-[15px] py-[18px]">
    <div className="font-mono text-[10px] uppercase tracking-wide text-faint">{label}</div>
    <div className="mt-1.5 text-[13.5px] font-medium text-heading">{value}</div>
    <div className="mt-0.5 text-[11px] text-muted">{sub}</div>
  </div>
);

export const AllocationStrip: React.FC<{
  employee: EmployeeView;
  lifecycleLabel: string;
  manager: string | null;
  managerRole: string | null;
}> = ({ employee, lifecycleLabel, manager, managerRole }) => {
  const { load, attention } = employee;
  const loadSub =
    load.state === 'assigned'
      ? 'assigned to projects'
      : load.state === 'frozen'
        ? 'frozen by lifecycle'
        : 'no active assignments';
  return (
    <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-3 lg:grid-cols-5">
      <Cell label="Lifecycle" value={lifecycleLabel} sub="owned · org-wide" />
      <Cell
        label="Assignments"
        value={employee.lifecycle === 'terminated' ? '—' : load.activeAssignments}
        sub={employee.lifecycle === 'terminated' ? 'archived' : loadSub}
      />
      <Cell
        label="Attention"
        value={attention.level === 'clear' ? 'Clear' : attention.level.replace('_', ' ')}
        sub={attention.detail ?? (attention.level === 'clear' ? 'no project flags' : 'needs a human')}
      />
      <Cell label="Manager" value={manager ?? '—'} sub={managerRole ?? (manager ? '' : 'unassigned')} />
      <Cell label="Cost · 7d" value="—" sub="no cost data" />
    </div>
  );
};

// ── Mandate & goals ─────────────────────────────────────────────────────────

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-[92px_1fr] items-start gap-2 px-4 py-2 text-xs">
    <div className="pt-px font-mono text-[10px] uppercase tracking-wide text-faint">{label}</div>
    <div className="text-body">{children}</div>
  </div>
);

export const MandateCard: React.FC<{ employee: EmployeeView; templateLabel: string | null }> = ({
  employee,
  templateLabel,
}) => (
  <ProfileCard icon={<ClipboardList size={14} />} title="Mandate & goals">
    <div className="border-b border-border px-4 py-3.5">
      <p className="text-[12.5px] leading-relaxed text-body">
        {employee.mandate || 'No mandate set for this employee.'}
      </p>
    </div>
    <div className="py-1.5">
      {employee.goals.length > 0 && (
        <MetaRow label="Goals">
          <ul className="m-0 list-disc pl-[15px]">
            {employee.goals.map((g, i) => (
              <li key={`${g}-${i}`} className="mb-0.5">
                {g}
              </li>
            ))}
          </ul>
        </MetaRow>
      )}
      {employee.reportingRules && <MetaRow label="Reporting">{employee.reportingRules}</MetaRow>}
      <MetaRow label="Tier">{employee.tier || '—'}</MetaRow>
      {templateLabel && (
        <MetaRow label="Runs on">
          Agent template <Chip tone="blue">{templateLabel}</Chip>
        </MetaRow>
      )}
    </div>
  </ProfileCard>
);

// ── Org permission grants ───────────────────────────────────────────────────

type GrantState = 'active' | 'expiring' | 'expired' | 'revoked' | 'pending';

interface GrantRow {
  id: string;
  name: string;
  sub: string;
  icon: 'key' | 'shield' | 'stack' | 'link';
  state: GrantState;
  daysLeft: number | null;
}

const GRANT_ICON = { key: Key, shield: Shield, stack: Layers, link: Link2 } as const;

function normaliseGrant(raw: Record<string, unknown>, i: number): GrantRow {
  const s = (k: string) => (typeof raw[k] === 'string' ? (raw[k] as string) : '');
  const resourceType = s('resourceType');
  const resourceId = s('resourceId');
  const action = s('action');
  const grantMode = s('grantMode');
  const reason = s('reason');
  const decision = s('decision');
  const expiresAt = s('expiresAt');
  const revokedAt = s('revokedAt');
  const lower = resourceType.toLowerCase();
  const icon: GrantRow['icon'] = lower.includes('secret')
    ? 'shield'
    : lower.includes('container')
      ? 'stack'
      : lower.includes('net') || lower.includes('http') || lower.includes('mcp')
        ? 'link'
        : 'key';
  let state: GrantState = 'active';
  let daysLeft: number | null = null;
  if (revokedAt) state = 'revoked';
  else if (decision === 'deny') state = 'pending';
  else if (expiresAt) {
    const delta = new Date(expiresAt).getTime() - Date.now();
    if (delta < 0) state = 'expired';
    else if (delta < 31 * 86_400_000) {
      state = 'expiring';
      daysLeft = Math.max(1, Math.round(delta / 86_400_000));
    }
  }
  return {
    id: typeof raw.id === 'string' || typeof raw.id === 'number' ? String(raw.id) : `grant-${i}`,
    name: `${resourceType || 'Permission'}${resourceId ? ` · ${resourceId}` : action ? ` · ${action}` : ''}`,
    sub: `${grantMode || 'allow'}${reason ? ` · ${reason}` : ''}`,
    icon,
    state,
    daysLeft,
  };
}

const STATE_CHIP: Record<GrantState, { tone: 'green' | 'purple' | 'neutral'; label: (d: number | null) => string }> = {
  active: { tone: 'green', label: () => 'active' },
  expiring: { tone: 'purple', label: (d) => (d ? `${d}d` : '30d') },
  expired: { tone: 'neutral', label: () => 'expired' },
  revoked: { tone: 'neutral', label: () => 'revoked' },
  pending: { tone: 'purple', label: () => 'pending' },
};

export const PermissionGrantsCard: React.FC<{
  grants: Array<Record<string, unknown>>;
  perProjectHref: string;
}> = ({ grants, perProjectHref }) => {
  const rows = grants.map((g, i) => normaliseGrant(g, i));
  return (
    <ProfileCard icon={<Shield size={14} />} title="Org permission grants" meta="durable">
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted">No durable org-level permission grants.</div>
      ) : (
        rows.map((row) => {
          const Icon = GRANT_ICON[row.icon];
          const chip = STATE_CHIP[row.state];
          return (
            <div
              key={row.id}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5 border-b border-border px-4 py-2.5 text-[12.5px] last:border-b-0"
            >
              <span className="grid h-[26px] w-[26px] place-items-center rounded-md bg-surface-alt text-muted">
                <Icon size={13} />
              </span>
              <div className="min-w-0">
                <div className="truncate font-medium text-heading">{row.name}</div>
                <div className="truncate font-mono text-[10px] text-faint">{row.sub}</div>
              </div>
              <Chip tone={chip.tone}>{chip.label(row.daysLeft)}</Chip>
            </div>
          );
        })
      )}
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5 bg-surface-alt px-4 py-2.5 text-[12.5px]">
        <span className="grid h-[26px] w-[26px] place-items-center rounded-md bg-surface-alt text-muted">
          <Shield size={13} />
        </span>
        <div className="min-w-0">
          <div className="font-medium text-heading">Per-project scope</div>
          <div className="font-mono text-[10px] text-faint">audited inside each project</div>
        </div>
        <Link to={perProjectHref} className="text-[11px] text-accent-text hover:underline">
          View →
        </Link>
      </div>
    </ProfileCard>
  );
};

// ── Project-scope callout ───────────────────────────────────────────────────

export const ScopeNote: React.FC<{ name: string; supervisionHref: string; supervisionLabel: string }> = ({
  name,
  supervisionHref,
  supervisionLabel,
}) => (
  <div className="flex items-start gap-3 rounded-xl border border-l-[3px] border-border border-l-accent bg-linear-to-br from-accent/8 to-transparent px-4 py-3.5">
    <MessageSquare size={18} className="mt-0.5 shrink-0 text-accent-text" />
    <div className="text-[12.5px] leading-relaxed text-body">
      <b className="text-heading">Supervision happens in project context.</b> The conversation, decision cards, and
      output reviews for {name} live on each project's workforce detail — not here.{' '}
      <Link to={supervisionHref} className="text-accent-text hover:underline">
        {supervisionLabel} →
      </Link>
    </div>
  </div>
);
